import React, { useEffect, useState } from 'react';
import PartnerRequests from './PartnerRequests';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import { ref, push, update, remove, onValue } from 'firebase/database';

// Partner types shown in the dropdown. Add more here as needed.
export const PARTNER_TYPES = ['Photographer', 'Stager', 'Title Company', 'Inspector', 'Lender', 'Insurance', 'Gifts'];

const EMPTY = { companyName: '', firstName: '', lastName: '', email: '', phone: '', address: '', partnerType: '' };

// Formats as 239-555-0100 while typing. Keeps only the first 10 digits.
const formatPhone = (value) => {
  const d = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};
const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const styles = {
  panel: { backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderRadius: '10px', padding: '28px', marginBottom: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  title: { fontSize: '20px', fontWeight: 700, margin: '0 0 22px', color: '#1a1a1a', fontFamily: FONT },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px' },
  group: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '12px', fontWeight: 700, color: '#8C6D1F', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: FONT },
  input: { padding: '12px', backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderRadius: '6px', color: '#1a1a1a', fontSize: '15px', fontFamily: FONT, width: '100%', boxSizing: 'border-box' },
  button: { marginTop: '20px', padding: '14px 24px', background: 'linear-gradient(180deg, #f6e7b4 0%, #d9b357 42%, #b98d2e 58%, #e8cc7a 100%)', color: '#1a1a1a', border: '1px solid #b8903a', borderRadius: '6px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT },
  errorBox: { backgroundColor: '#fbeeee', border: '1px solid #b00020', borderRadius: '6px', padding: '12px', marginBottom: '18px', color: '#b00020', fontSize: '14px', fontFamily: FONT },
  successBox: { backgroundColor: '#eef7ee', border: '1px solid #2e7d32', borderRadius: '6px', padding: '12px', marginBottom: '18px', color: '#2e7d32', fontSize: '14px', fontWeight: 600, fontFamily: FONT },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px', fontFamily: FONT },
  th: { textAlign: 'left', fontSize: '12px', color: '#8C6D1F', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '12px 8px', borderBottom: '2px solid #d8c9a8', fontWeight: 700 },
  td: { padding: '14px 8px', borderBottom: '1px solid #efe6d2', color: '#1a1a1a', verticalAlign: 'middle' },
  editBtn: { background: '#ffffff', border: '1px solid #8C6D1F', color: '#8C6D1F', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, marginRight: '8px' },
  removeBtn: { background: '#ffffff', border: '1px solid #d8c9a8', color: '#7f7f7f', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' },
  cancelBtn: { marginTop: '20px', padding: '14px 24px', backgroundColor: '#ffffff', color: '#1a1a1a', border: '1px solid #d8c9a8', borderRadius: '6px', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: FONT },
  empty: { color: '#7f7f7f', fontSize: '14px', margin: 0, fontFamily: FONT },
  tableWrap: { overflowX: 'auto' },
};

const FIELDS = [
  { name: 'companyName', label: 'Company Name' },
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'email', label: 'Email Address', type: 'email' },
  { name: 'phone', label: 'Phone Number', type: 'tel', placeholder: '239-555-0100' },
  { name: 'address', label: 'Address (optional)', placeholder: 'Street, city, state, zip' },
];

export default function PartnersSection() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const partnersRef = ref(database, 'partners');
    const unsubscribe = onValue(
      partnersRef,
      (snap) => {
        const val = snap.val() || {};
        const list = Object.entries(val)
          .map(([id, p]) => ({ id, ...p }))
          .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setPartners(list);
      },
      (err) => {
        console.error('Could not load partners:', err);
        setError('Could not load partners.');
      }
    );
    return () => unsubscribe();
  }, [user]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === 'phone' ? formatPhone(value) : value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const required = ['companyName', 'firstName', 'lastName', 'email', 'partnerType'];
    if (required.some((k) => !String(form[k] || '').trim())) {
      setError('Please fill in company, first and last name, email, and partner type.');
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await update(ref(database, `partners/${editingId}`), {
          ...form,
          updatedAt: new Date().toISOString(),
        });
        setSuccess(`${form.firstName} ${form.lastName} updated.`);
        setEditingId(null);
      } else {
        await push(ref(database, 'partners'), {
          ...form,
          createdAt: new Date().toISOString(),
        });
        setSuccess(`${form.firstName} ${form.lastName} added as a ${form.partnerType}.`);
      }
      setForm(EMPTY);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Could not save partner:', err);
      setError('Could not save partner. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setError('');
    setSuccess('');
    setEditingId(p.id);
    setForm({ ...EMPTY, ...p });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setSuccess('');
  };

  const removePartner = async (id) => {
    if (!window.confirm('Remove this partner?')) return;
    try {
      await remove(ref(database, `partners/${id}`));
    } catch (err) {
      console.error('Could not remove partner:', err);
      setError('Could not remove partner.');
    }
  };

  return (
    <div>
      <div style={styles.panel}>
        <h2 style={styles.title}>{editingId ? 'Edit Partner' : 'Add New Partner'}</h2>
        <form onSubmit={save}>
          {error && <div style={styles.errorBox}>{error}</div>}
          {success && <div style={styles.successBox}>{success}</div>}
          <div style={styles.grid}>
            {FIELDS.map((f) => (
              <div key={f.name} style={styles.group}>
                <label style={styles.label} htmlFor={`partner-${f.name}`}>{f.label}</label>
                <input
                  id={`partner-${f.name}`}
                  name={f.name}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.name]}
                  onChange={change}
                  style={styles.input}
                />
              </div>
            ))}
            <div style={styles.group}>
              <label style={styles.label} htmlFor="partner-partnerType">Partner Type</label>
              <select
                id="partner-partnerType"
                name="partnerType"
                value={form.partnerType}
                onChange={change}
                style={styles.input}
              >
                <option value="">Select One</option>
                {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button type="submit" disabled={saving} style={{ ...styles.button, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Partner'}
            </button>
            {editingId && (
              <button type="button" onClick={cancelEdit} style={styles.cancelBtn}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div style={styles.panel}>
        <PartnerRequests />
        <h2 style={styles.title}>Partners</h2>
        {partners.length === 0 ? (
          <p style={styles.empty}>No partners yet.</p>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Address</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id}>
                    <td style={styles.td}>{p.companyName}</td>
                    <td style={styles.td}>{p.firstName} {p.lastName}</td>
                    <td style={styles.td}>{p.partnerType}</td>
                    <td style={styles.td}>{p.email}</td>
                    <td style={styles.td}>{p.phone}</td>
                    <td style={styles.td}>{p.address}</td>
                    <td style={styles.td}>
                      <button type="button" onClick={() => startEdit(p)} style={styles.editBtn}>Edit</button>
                      <button type="button" onClick={() => removePartner(p.id)} style={styles.removeBtn}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
